import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@/providers/RouterShim';
import type { ApiResponse } from '../../@types/ApiResponse';
import type { RequestStateQuery } from '../../@types/RequestStateQuery';
import { postJSON } from '../../fetch';
import useAuth from '../../hooks/useAuth';
import { VIRTUAL_VEHICLES_QUERY_KEY } from './useVirtualVehicles';

interface CreateVirtualAdPayload {
  vehicleId: string;
  expectedDelivery: {
    from?: number;
    to?: number;
    unit: string;
  };
}

interface CreateVirtualAdResponse {
  id: string;
}

interface CreateVirtualAdOptions {
  onStartPolling?: () => void;
}

const useCreateVirtualAd = (
  options?: CreateVirtualAdOptions,
): [(payload: CreateVirtualAdPayload) => void, RequestStateQuery, string | undefined] => {
  const { getToken } = useAuth();
  const { branchId } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (payload: CreateVirtualAdPayload) =>
      await postJSON<ApiResponse<CreateVirtualAdResponse>>(
        `${process.env.NEXT_PUBLIC_VIRTUAL_VEHICLE_URI}/commands/${branchId}/virtual-ads`,
        getToken()!,
        payload,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [VIRTUAL_VEHICLES_QUERY_KEY] });

      // Start polling if callback is provided (new virtual ads may need blocket sync)
      if (options?.onStartPolling) {
        options.onStartPolling();
      }
    },
  });

  const { mutate, isPending, isSuccess, isError, data } = mutation;

  return [
    (payload: CreateVirtualAdPayload) => mutate(payload),
    { isLoading: isPending, isSuccess, isError },
    data?.response.id,
  ];
};

export default useCreateVirtualAd;
